/*
  Edit a personalised basket line in place.

  Every personalised product — the planners, Made in my town, Keep Calm, the street signs,
  Create Your Own and its business twin — writes a hidden `_edit_url` property when it adds to
  the basket. blocks/btf-cart-extras.liquid turns that into the line's Edit link, appending the
  line key: /pages/create-your-own-magnetic-board?edit=<key>.

  Arriving with ?edit=<key>, this script:

    1. reads the line back from /cart.js,
    2. restores it, and
    3. remembers the key so the next add replaces that line rather than adding a second.

  Restoring splits by how a product is built. The form-based ones (planners, town, Keep Calm,
  signs) carry their wording and choices as ordinary inputs named properties[...], and the
  property name IS the input name, so they restore generically here with no per-product code.
  Create Your Own and the business tool hold their state in JavaScript instead, so they read
  `btfEditLine.line` themselves and rebuild the board from its `_crop` property.

  If anything fails — the line has gone, the cart is empty, the fetch is blocked — the page is
  left exactly as it would have loaded, which is a new board rather than a broken one.
*/
(function () {
  const params = new URLSearchParams(window.location.search);
  const key = params.get('edit') || '';

  const api = {
    key: key,
    active: Boolean(key),
    line: null,
    ready: null,
  };
  window.btfEditLine = api;

  /* ----------------------------------------------------------------- stamp */

  // Any form that already carries a properties[...] field is a personalised product, so it gets
  // a hidden _edit_url naming the page it was configured on. Doing it here rather than in each
  // product's Liquid keeps the five field blocks untouched. If scripts fail the property is
  // simply absent and the basket falls back to linking the product, as it did before.
  function stamp() {
    for (const field of document.querySelectorAll('[name^="properties["]')) {
      const form = field.form || field.closest('form');
      if (!form || form.querySelector('[name="properties[_edit_url]"]')) continue;
      const hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = 'properties[_edit_url]';
      hidden.value = window.location.pathname;
      form.appendChild(hidden);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', stamp, { once: true });
  } else {
    stamp();
  }
  // Horizon re-renders sections after a variant change, which drops the injected field.
  document.addEventListener('change', stamp);

  /* -------------------------------------------------- straight to the basket */

  const cartUrl = () => {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.replace(/\/$/, '') + '/cart';
  };

  const cartCount = () =>
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((c) => (c ? c.item_count : -1))
      .catch(() => -1);

  // A personalised board is a one-off, so the customer is sent to the basket to check the
  // wording rather than left on the page wondering whether it worked. Ordinary products keep
  // Horizon's behaviour, which is why this only fires for a form carrying properties[...].
  // Create Your Own and the business tool post their own add and navigate themselves.
  // The baseline is taken when the page loads, not when the form is submitted. Reading it on
  // submit races Horizon's own add, which can land first and make the new count look unchanged.
  let baseline = -1;
  let adding = false;
  const trackBaseline = () => {
    if (adding) return;
    cartCount().then((c) => {
      if (!adding && c >= 0) baseline = c;
    });
  };
  trackBaseline();
  // The basket can change without this form — a removal in the drawer, another tab — so the
  // baseline is kept current while idle rather than trusted from page load alone.
  setInterval(trackBaseline, 3000);

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!form.querySelector('[name^="properties["]')) return;
      if (adding) return;
      adding = true;
      const started = Date.now();
      const look = () => {
        if (Date.now() - started > 20000) {
          adding = false;
          return;
        }
        cartCount().then((now) => {
          if (baseline >= 0 && now > baseline) {
            // Saving an edit: the new line is in, so the one it replaces comes out, then the basket
            const done = () => window.location.assign(cartUrl());
            if (api.active) api.replaceOriginal().then(done, done);
            else done();
          } else setTimeout(look, 400);
        });
      };
      setTimeout(look, 500);
    },
    true
  );


  if (!key) {
    api.ready = Promise.resolve(null);
    return;
  }

  /* ------------------------------------------------------------------ read */

  api.ready = fetch('/cart.js', { headers: { Accept: 'application/json' } })
    .then((r) => (r.ok ? r.json() : null))
    .then((cart) => {
      const items = (cart && cart.items) || [];
      api.line = items.find((i) => i.key === key) || null;
      if (api.line) {
        restoreForms(api.line);
        scrollToTool();
        // Changing the size makes Horizon re-render the section; one repeat puts the wording
        // back into the fields that replaced the originals.
        setTimeout(() => restoreForms(api.line), 900);
      }
      return api.line;
    })
    .catch(() => null);

  /* --------------------------------------------------------------- restore */

  function fire(el, types) {
    for (const t of types) el.dispatchEvent(new Event(t, { bubbles: true }));
  }

  // The form-based products: fill every properties[...] field the line has a value for, then
  // let each product's own listeners redraw the board from the change.
  function restoreForms(line) {
    const props = line.properties || {};

    // The size goes first: Horizon re-renders the section when the variant changes, which would
    // otherwise wipe the wording we had just put back.
    selectVariant(line.variant_id);
    selectOptions(line);

    for (const el of document.querySelectorAll('[name^="properties["]')) {
      const match = /^properties\[(.*)\]$/.exec(el.getAttribute('name') || '');
      if (!match) continue;
      const name = match[1];
      if (!name || name.charAt(0) === '_') continue; // internal properties are not the customer's
      if (!Object.prototype.hasOwnProperty.call(props, name)) continue;
      const value = props[name] == null ? '' : String(props[name]);

      if (el.type === 'radio' || el.type === 'checkbox') {
        const on = el.value === value;
        if (el.checked !== on) {
          el.checked = on;
          fire(el, ['change']);
        }
      } else if (el.value !== value) {
        el.value = value;
        fire(el, ['input', 'change']);
      }
    }

  }

  // Horizon's own variant picker is a radio group per option, named "<Option>-<hash>" and
  // valued by the option itself ("75cm (w) x 100cm (h)"), so the variant id never appears.
  // Matching on the line's option values is what actually moves it.
  function selectOptions(line) {
    for (const opt of line.options_with_values || []) {
      const name = String((opt && opt.name) || '');
      const value = String((opt && opt.value) || '');
      if (!name || !value) continue;
      const prefix = name + '-';
      for (const radio of document.querySelectorAll('input[type="radio"]')) {
        if (radio.name.indexOf(prefix) !== 0 || radio.value !== value || radio.checked) continue;
        radio.checked = true;
        fire(radio, ['change']);
      }
      for (const select of document.querySelectorAll('select')) {
        if (select.name.indexOf(prefix) !== 0 || select.value === value) continue;
        if (!select.querySelector('option[value="' + CSS.escape(value) + '"]')) continue;
        select.value = value;
        fire(select, ['change']);
      }
    }
  }

  // The size the customer chose is the variant, not a property. Pick it wherever the product's
  // own picker exposes it — a select, a radio set, or the form's hidden id field.
  function selectVariant(variantId) {
    if (!variantId) return;
    const id = String(variantId);

    for (const select of document.querySelectorAll('select[name="id"]')) {
      if (select.value !== id && select.querySelector('option[value="' + id + '"]')) {
        select.value = id;
        fire(select, ['change']);
      }
    }
    for (const radio of document.querySelectorAll('input[type="radio"][name="id"]')) {
      if (radio.value === id && !radio.checked) {
        radio.checked = true;
        fire(radio, ['change']);
      }
    }
    for (const hidden of document.querySelectorAll('input[type="hidden"][name="id"]')) {
      if (hidden.value !== id) {
        hidden.value = id;
        fire(hidden, ['change']);
      }
    }
  }

  // Coming back from the basket, the customer wants the board, not the top of the page. The
  // personalisation sits well down a long page on every one of these products.
  function scrollToTool() {
    const tool =
      document.querySelector('btf-cyo-tool, btf-biz-cyo') ||
      (document.querySelector('[name^="properties["]') || {}).closest?.('.shopify-section, section, form');
    if (!tool || !tool.scrollIntoView) return;
    // After the restore, so a variant re-render does not move the target underneath us.
    setTimeout(() => {
      try {
        tool.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch (e) {
        tool.scrollIntoView();
      }
    }, 1200);
  }

  /* --------------------------------------------------------------- replace */

  // Called by each product once its own add has succeeded. Removing the original afterwards
  // rather than before means a failed add leaves the customer's basket untouched.
  // The form-based products get this from the submit watcher above once their add has landed.
  //
  // A line's key is its variant and properties, so an edit saved unchanged lands on the same
  // line as the original (Shopify adds to its quantity) rather than making a new one. That line
  // goes back to the quantity it had; removing it would empty the customer's basket of the board.
  api.replaceOriginal = function () {
    if (!api.key) return Promise.resolve(false);
    const gone = api.key;
    const had = (api.line && api.line.quantity) || 1;
    api.key = '';
    api.active = false;
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((cart) => {
        const line = ((cart && cart.items) || []).find((i) => i.key === gone);
        if (!line) return true;
        const quantity = line.quantity > had ? had : 0;
        return fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: gone, quantity: quantity }),
        }).then((r) => r.ok);
      })
      .catch(() => false);
  };
})();
